import { useContext, useState } from 'react';
import moment from 'moment';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _parseInt from 'lodash/parseInt';
import _pick from 'lodash/pick';
import _find from 'lodash/find';
import { AjaxContext, AppContext, EMPTY_OBJECT, UserContext } from 'commons-ui';
import { UserDetailsContext } from 'context/userDetails';
import { GAME_NAMES, GAME_NAME_OPTIONS } from 'pages/constants';
import { ACTION, PAGE_NAMES, USER_PROFILE_RESOURCES } from 'constant';
import { IRefundSuggestionProps } from '../types';

const convertTimeStringToUnix = (timeString: string): number => {
	const time = moment(timeString, 'DD-MM-YYYY hh:mm:ss.SSS A').valueOf();
	return time;
};
const getCTParams = (props: IRefundSuggestionProps) => _pick(props, ['winnings', 'score', 'resultData', 'inning', 'entryFee']);
const getLudonxParams = (props: IRefundSuggestionProps) => _pick(props, ['playingAttempt']);
const GAMEPLAY_THRESHOLD_SECONDS: number = 480;
const gameSpecificBodyParamsFunctorMap = {
	[GAME_NAME_OPTIONS[GAME_NAMES.CT]]: getCTParams,
	[GAME_NAME_OPTIONS[GAME_NAMES.LUDONX]]: getLudonxParams
};
const getNxGameEndTime = (time: number) => {
	return moment(time).add(GAMEPLAY_THRESHOLD_SECONDS, 'seconds').valueOf();
};

const useService = (props: IRefundSuggestionProps) => {
	const { gameName = GAME_NAMES.LUDO, ltid, tableId, gameEndTime, gameUid, registeredOn, gameStartTime, entryFee } = props;
	const [refundSuggestionDetails, setRefundSuggestionDetails] = useState({});
	const { postData } = useContext(AjaxContext);
	const {
		app: { appName }
	} = useContext(AppContext);
	const {
		selectedUser: { uid, ludoUserId }
	} = useContext(UserDetailsContext);
	const { permissionsConfig } = useContext(UserContext);
	const gameSpecificParams = gameSpecificBodyParamsFunctorMap[gameName as string]
		? gameSpecificBodyParamsFunctorMap[gameName as string](props)
		: EMPTY_OBJECT;

	const refundSuggestion = async () => {
		const permission = _find(permissionsConfig, { actionName: ACTION.refundSuggestion });
		const result = await postData({
			endpoint: `/dashboard/refundSuggestion/${PAGE_NAMES.userProfile}-${USER_PROFILE_RESOURCES.gameplay}-refundSuggestion`,
			queryParams: {
				appName
			},
			body: {
				entryFee,
				zupeeUserId: uid,
				gameName,
				// @ts-ignore
				ltid: _parseInt(ltid),
				tableId,
				registeredOn: convertTimeStringToUnix(registeredOn),
				startTime:
					gameName === GAME_NAME_OPTIONS[GAME_NAMES.LUDONX]
						? convertTimeStringToUnix(registeredOn)
						: convertTimeStringToUnix(gameStartTime),
				endTime:
					gameName === GAME_NAME_OPTIONS[GAME_NAMES.LUDONX]
						? getNxGameEndTime(convertTimeStringToUnix(registeredOn))
						: convertTimeStringToUnix(gameEndTime),
				gameUid,
				ludoUserId,
				...gameSpecificParams
			},
			customHeaders: {
				isMasked: permission?.isMasked || false
			}
		});

		return _get(result, ['data', 'data']);
	};

	const getRefundSuggestionDetails = async () => {
		if (!_isEmpty(refundSuggestionDetails)) {
			return refundSuggestionDetails;
		}
		const data = await refundSuggestion();
		setRefundSuggestionDetails({
			refund: data.refund,
			reason: data.reason,
			warning: data.warning,
			leniencyResult: data.leniencyResult,
			instantRefundResult: data.instantRefundResult,
			refundSuggestionRuleSetUsed: data.refundSuggestionRuleSetUsed,
			refundType: data.refundType
		});
		return {
			refund: data.refund,
			reason: data.reason,
			warning: data.warning,
			refundSuggestionRuleSetUsed: data.refundSuggestionRuleSetUsed,
			isLenientRefund: _get(data, ['leniencyResult', 'isLenientRefund'], false) ? 'YES' : 'NO',
			leniencyRefundReason: _get(data, ['leniencyResult', 'reason'], null),
			isInstantRSYesRefund: _get(data, ['instantRefundResult', 'isInstantRefund'], false) ? 'YES' : 'NO',
			instantRSYesRefundReason: _get(data, ['instantRefundResult', 'reason'], null),
			refundType: _get(data, ['refundType'], null)
		};
	};

	return {
		getRefundSuggestionDetails,
		refundSuggestionDetails
	};
};

export default useService;
